import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import clsx from 'clsx';
import styles from './styles.module.css';

interface DropdownProps extends React.PropsWithChildren {
  button: React.ReactNode;
  variant?: string;
  isDropdown: boolean;
  setIsDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchQuery?: React.Dispatch<React.SetStateAction<string>>;
}

export interface DropdownRef {
  close: () => void;
}

export const Dropdown = forwardRef<DropdownRef, DropdownProps>((props, ref) => {
  const { isDropdown, setIsDropdown, setSearchQuery } = props;
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    if (setSearchQuery) setSearchQuery('');
    setIsDropdown(!isDropdown);
  };

  const closeDropdown = useCallback(() => {
    setIsDropdown(false);
  }, [setIsDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  useImperativeHandle(
    ref,
    () => ({
      close: closeDropdown,
    }),
    [closeDropdown]
  );

  return (
    <div className={styles.container} ref={dropdownRef}>
      <div
        className={clsx(styles.dropdown, isDropdown && styles.open)}
        onClick={(e) => {
          e.stopPropagation();
          toggleDropdown();
        }}
      >
        {props.button}
      </div>
      {isDropdown && (
        <div
          className={clsx(
            styles.content,
            props.variant === 'NoResults' && styles.no_results
          )}
        >
          {props.children}
        </div>
      )}
    </div>
  );
});

Dropdown.displayName = 'CollectionCasinoDropdown';
